import { useState, useEffect, useRef } from "react";
import { useNavigate, useNavigationType } from "react-router-dom";
import DiscussionCard from "../components/DiscussionCard";
import { getMovies, getTrending, getDiscussions, getShows } from "../api";
import GlobeHero from "../components/GlobeHero";
import { useAuth } from "../context/AuthContext";

const GENRES = ["all", "drama", "comedy", "romance", "action", "horror"];
const LANGUAGES = [
  { key: "",   label: "All"     },
  { key: "ne", label: "Nepali"  },
  { key: "hi", label: "Hindi"   },
  { key: "en", label: "English" },
];

// ─── Poster card for horizontal rows ──────────────────────────────────────────

function PosterCard({ movie, basePath = "/movie" }) {
  const navigate = useNavigate();
  const path = movie.media_type === "show" ? "/show" : basePath;
  const initials = movie.title
    ? movie.title.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <div
      onClick={() => movie.id && navigate(`${path}/${movie.id}`)}
      style={{
        flexShrink: 0, width: 130,
        cursor: movie.id ? "pointer" : "default",
        transition: "transform 0.18s",
      }}
      onMouseEnter={e => { if (movie.id) e.currentTarget.style.transform = "translateY(-4px)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{
        width: 130, height: 190, borderRadius: 10, overflow: "hidden",
        background: "#161618",
        boxShadow: "0 4px 18px rgba(0,0,0,0.45)",
        border: "0.5px solid #252528",
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 8, position: "relative",
      }}>
        {movie.poster_url ? (
          <img
            src={movie.poster_url} alt={movie.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 28, fontWeight: 800, color: "#2a2a32",
          }}>{initials}</span>
        )}
        {movie.score && (
          <div style={{
            position: "absolute", bottom: 6, right: 6,
            background: "rgba(8,8,10,0.88)", backdropFilter: "blur(4px)",
            borderRadius: 6, padding: "2px 7px",
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 11, fontWeight: 800,
            color: "#FF5C35",
          }}>{movie.score}</div>
        )}
        {!movie.id && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.42)",
            display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
            padding: 6,
          }}>
            <div style={{ fontSize: 9, color: "#55555e", background: "#111", borderRadius: 4, padding: "1px 5px" }}>
              not in db
            </div>
          </div>
        )}
      </div>
      <div style={{
        fontFamily: "'Bricolage Grotesque', sans-serif",
        fontSize: 12, fontWeight: 600, color: "#c0c0c8",
        lineHeight: 1.3,
        display: "-webkit-box", WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>{movie.title}</div>
      <div style={{ fontSize: 10, color: "#45454e", marginTop: 2 }}>{movie.year}</div>
    </div>
  );
}

// ─── Horizontal scroll row ─────────────────────────────────────────────────────

function ScrollRow({ title, movies, loading }) {
  const rowRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  function updateEdges() {
    const el = rowRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 4);
  }

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    setAtStart(true);
    setAtEnd(el.scrollWidth <= el.clientWidth + 4);
  }, [movies]);

  function scrollRow(dir) {
    rowRef.current?.scrollBy({ left: dir * 420, behavior: "smooth" });
  }

  const arrowStyle = (side) => ({
    position: "absolute", top: "50%", [side]: 0,
    transform: "translateY(-50%)", zIndex: 10,
    width: 36, height: 60,
    background: "rgba(11,11,12,0.85)", backdropFilter: "blur(6px)",
    border: "0.5px solid #252528",
    borderRadius: side === "left" ? "0 8px 8px 0" : "8px 0 0 8px",
    color: "#c0c0c0", fontSize: 22, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.15s, color 0.15s",
  });

  return (
    <div
      style={{ maxWidth: 1280, margin: "0 auto", padding: "0 clamp(16px, 4vw, 20px)", marginBottom: 40 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <h2 style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: 17, fontWeight: 800, color: "#f0f0f0",
          letterSpacing: "-0.3px",
        }}>{title}</h2>
        <span style={{ fontSize: 11, color: "#3a3a44" }}>{movies.length} films</span>
      </div>
      {loading ? (
        <div style={{ height: 190, display: "flex", alignItems: "center", color: "#3a3a44", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          loading…
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          {hovered && !atStart && (
            <button
              onClick={() => scrollRow(-1)}
              style={arrowStyle("left")}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(30,30,34,0.95)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(11,11,12,0.85)"; e.currentTarget.style.color = "#c0c0c0"; }}
            >‹</button>
          )}
          {hovered && !atEnd && (
            <button
              onClick={() => scrollRow(1)}
              style={arrowStyle("right")}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(30,30,34,0.95)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(11,11,12,0.85)"; e.currentTarget.style.color = "#c0c0c0"; }}
            >›</button>
          )}
          <div
            ref={rowRef}
            onScroll={updateEdges}
            style={{
              display: "flex", gap: 14, overflowX: "auto", paddingBottom: 12,
              scrollbarWidth: "none", msOverflowStyle: "none",
            }}
          >
            {movies.map((m, i) => <PosterCard key={m.id ?? m.tmdb_id ?? i} movie={m} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Movie grid card (full info) ───────────────────────────────────────────────

function MovieCard({ movie, basePath = "/movie" }) {
  const navigate = useNavigate();
  const path = movie.media_type === "show" ? "/show" : basePath;
  const initials = movie.title
    ? movie.title.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  const firstGenre = movie.genres?.[0]?.trim() || "";

  return (
    <div
      onClick={() => navigate(`${path}/${movie.id}`)}
      style={{
        background: "#0f0f12", border: "0.5px solid #1e1e22",
        borderRadius: 14, overflow: "hidden", cursor: "pointer",
        transition: "border-color 0.2s, transform 0.2s, box-shadow 0.2s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = "rgba(255,92,53,0.45)";
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 8px 28px rgba(255,92,53,0.1)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "#1e1e22";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        height: 130, background: "#161618", position: "relative",
        overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {movie.poster_url ? (
          <img src={movie.poster_url} alt={movie.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 30, fontWeight: 800, color: "#2a2a32" }}>
            {initials}
          </span>
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 50%, rgba(9,9,11,0.55) 100%)", pointerEvents: "none" }} />
      </div>
      <div style={{ padding: "11px 13px 13px" }}>
        <div style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontWeight: 700, fontSize: 12, color: "#e8e8ee",
          lineHeight: 1.3, marginBottom: 3,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{movie.title}</div>
        <div style={{ fontSize: 10, color: "#45454e", marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {movie.year}{firstGenre ? ` · ${firstGenre}` : ""}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
          <span style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 19, fontWeight: 800,
            background: "linear-gradient(135deg, #FF5C35, #ff8a68)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>{movie.score ?? "—"}</span>
          <span style={{ fontSize: 10, color: "#45454e" }}>/10</span>
        </div>
      </div>
    </div>
  );
}

// ─── Home ──────────────────────────────────────────────────────────────────────

const CONTENT_TYPES = [
  { key: "movies", label: "Movies" },
  { key: "series", label: "Series" },
  { key: "anime",  label: "Anime"  },
];

const HOME_STATE_VER = 2;

function readHomeState() {
  try {
    const s = JSON.parse(sessionStorage.getItem("homeState") || "{}");
    return s._v === HOME_STATE_VER ? s : {};
  } catch { return {}; }
}

export default function Home() {
  const navigationType = useNavigationType();
  const { token } = useAuth();

  const [contentType, setContentType] = useState(() => {
    if (navigationType !== "POP") return "movies";
    return readHomeState().contentType || "movies";
  });
  const [activeLang, setActiveLang] = useState(() => {
    if (navigationType !== "POP") return "";
    return readHomeState().activeLang ?? "";
  });
  const [activeGenre, setActiveGenre] = useState(() => {
    if (navigationType !== "POP") return "all";
    return readHomeState().activeGenre || "all";
  });

  const [items,       setItems]       = useState([]);
  const [totalItems,  setTotalItems]  = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [trending,          setTrending]          = useState([]);
  const [trendingLoading,   setTrendingLoading]   = useState(true);
  const [recentDiscussions, setRecentDiscussions] = useState([]);

  const movieGridRef = useRef(null);
  const scrollRestored = useRef(false);
  const navigate = useNavigate();

  // Fetch trending + recent discussions once on mount
  useEffect(() => {
    getTrending()
      .then(data => setTrending(data.movies || []))
      .catch(() => setTrending([]))
      .finally(() => setTrendingLoading(false));
    getDiscussions({ per_page: 3 }, token)
      .then(data => setRecentDiscussions(data.discussions || []))
      .catch(() => setRecentDiscussions([]));
  }, []);

  // Fetch content when type, language, genre, or search changes
  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
    setItems([]);

    let fetchFn;
    const params = { per_page: 60, page: 1 };

    if (contentType === "movies") {
      params.language = activeLang;
      if (activeGenre !== "all") params.genre = activeGenre;
      fetchFn = getMovies(params).then(d => ({ items: d.movies || [], total: d.total || 0, pages: d.pages || 1 }));
    } else if (contentType === "anime") {
      params.anime = "true";
      fetchFn = getShows(params).then(d => ({ items: d.shows || [], total: d.total || 0, pages: d.pages || 1 }));
    } else {
      params.language = activeLang;
      fetchFn = getShows(params).then(d => ({ items: d.shows || [], total: d.total || 0, pages: d.pages || 1 }));
    }

    fetchFn
      .then(({ items: fetched, total, pages }) => {
        setItems(fetched);
        setTotalItems(total);
        setTotalPages(pages);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [contentType, activeLang, activeGenre]);

  // Keep homeState in sync so the lazy initializer always gets fresh values
  useEffect(() => {
    sessionStorage.setItem("homeState", JSON.stringify({ _v: HOME_STATE_VER, contentType, activeLang, activeGenre }));
  }, [contentType, activeLang, activeGenre]);

  // Save scroll position only when meaningfully scrolled (avoids saving 0 on StrictMode cleanup)
  useEffect(() => {
    return () => {
      if (window.scrollY > 50) {
        sessionStorage.setItem("homeScrollY", String(window.scrollY));
      } else {
        sessionStorage.removeItem("homeScrollY");
      }
    };
  }, []);

  // Restore scroll — only on back navigation, after both data fetches complete
  useEffect(() => {
    if (loading || trendingLoading || scrollRestored.current) return;
    if (navigationType !== "POP") {
      sessionStorage.removeItem("homeScrollY");
      return;
    }
    const saved = sessionStorage.getItem("homeScrollY");
    if (!saved) return;
    scrollRestored.current = true;
    sessionStorage.removeItem("homeScrollY");
    setTimeout(() => window.scrollTo({ top: parseInt(saved, 10), behavior: "instant" }), 80);
  }, [loading, trendingLoading]); // eslint-disable-line

  function loadMore() {
    const next = currentPage + 1;
    setLoadingMore(true);

    let fetchFn;
    const params = { per_page: 60, page: next };

    if (contentType === "movies") {
      params.language = activeLang;
      if (activeGenre !== "all") params.genre = activeGenre;
      fetchFn = getMovies(params).then(d => d.movies || []);
    } else if (contentType === "anime") {
      params.anime = "true";
      fetchFn = getShows(params).then(d => d.shows || []);
    } else {
      params.language = activeLang;
      fetchFn = getShows(params).then(d => d.shows || []);
    }

    fetchFn
      .then(newItems => { setItems(prev => [...prev, ...newItems]); setCurrentPage(next); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }

  const newReleases = contentType === "movies" ? items.filter(m => m.year >= 2024) : [];
  const basePath = contentType === "movies" ? "/movie" : "/show";

  function gridHeading() {
    if (contentType === "anime") return "anime series";
    // activeLang="" means "All" — produce no language prefix so we say "all films"
    const langLabel = activeLang ? (LANGUAGES.find(l => l.key === activeLang)?.label || "") : "";
    const langPart  = langLabel ? `${langLabel} ` : "";
    if (contentType === "series") return langPart ? `${langPart}series` : "all series";
    if (activeGenre !== "all")    return langPart ? `${activeGenre} · ${langPart}films` : `${activeGenre} films`;
    return langPart ? `${langPart}films` : "all films";
  }

  return (
    <div style={{ minHeight: "100vh" }}>

      {/* ── Globe Hero ── */}
      <div className="globe-hero-container" style={{ width: "100%", height: "calc(100vh - 56px)", minHeight: 560, overflowX: "hidden" }}>
        <GlobeHero
          onExplore={() => movieGridRef.current?.scrollIntoView({ behavior: "smooth" })}
          onJoinDiscussion={() => navigate("/discussions")}
        />
      </div>

      {/* ── Content type switcher ────────────────────────────────────────────── */}
      <div style={{ borderTop: "0.5px solid #1a1a1e", borderBottom: "0.5px solid #1a1a1e", background: "#0b0b0c" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 clamp(16px, 4vw, 20px)" }}>
          <div style={{ display: "flex", gap: 0 }}>
            {CONTENT_TYPES.map(ct => (
              <button key={ct.key}
                onClick={() => { setContentType(ct.key); setActiveGenre("all"); }}
                style={{
                  padding: "13px 22px", fontSize: 13, fontWeight: 600,
                  background: "transparent", border: "none",
                  borderBottom: contentType === ct.key ? "2px solid #FF5C35" : "2px solid transparent",
                  color: contentType === ct.key ? "#FF5C35" : "#55555e",
                  cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: "color 0.15s",
                }}
              >{ct.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Trending row (movies only) ───────────────────────────────────────── */}
      {contentType === "movies" && (
        <div style={{ paddingTop: 36 }}>
          <ScrollRow title="trending this week 🔥" movies={trending} loading={trendingLoading} />
        </div>
      )}

      {/* ── New releases row (movies only) ───────────────────────────────────── */}
      {contentType === "movies" && !loading && newReleases.length > 0 && (
        <ScrollRow title="new releases" movies={newReleases} loading={false} />
      )}

      {/* ── Language + genre filters ─────────────────────────────────────────── */}
      {contentType !== "anime" && (
        <div style={{ borderTop: "0.5px solid #1a1a1e", borderBottom: "0.5px solid #1a1a1e" }}>
          <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 clamp(16px, 4vw, 20px)" }}>
            <div className="filter-bar" style={{ display: "flex", alignItems: "center", gap: 0, padding: "12px 0" }}>
              {/* Language tabs */}
              <div className="filter-lang-group" style={{ display: "flex", gap: 2, marginRight: 20, borderRight: "0.5px solid #1e1e22", paddingRight: 20 }}>
                {LANGUAGES.map(l => (
                  <button key={l.key} onClick={() => { setActiveLang(l.key); setActiveGenre("all"); }}
                    style={{
                      fontSize: 12, padding: "6px 16px", borderRadius: 20,
                      border: `0.5px solid ${activeLang === l.key ? "#FF5C35" : "#252528"}`,
                      background: activeLang === l.key ? "#FF5C35" : "transparent",
                      color: activeLang === l.key ? "#fff" : "#55555e",
                      cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: activeLang === l.key ? 700 : 400,
                      transition: "all 0.15s",
                    }}
                  >{l.label}</button>
                ))}
              </div>
              {/* Genre tabs (movies only) */}
              {contentType === "movies" && (
                <div style={{ display: "flex", gap: 6, overflowX: "auto" }}>
                  {GENRES.map(g => (
                    <button key={g} onClick={() => setActiveGenre(g)} style={{
                      fontSize: 11, padding: "5px 14px", borderRadius: 20,
                      border: `0.5px solid ${activeGenre === g ? "rgba(255,92,53,0.5)" : "#252528"}`,
                      background: activeGenre === g ? "rgba(255,92,53,0.1)" : "transparent",
                      color: activeGenre === g ? "#FF5C35" : "#55555e",
                      cursor: "pointer", whiteSpace: "nowrap",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      transition: "all 0.15s",
                    }}>{g}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Content grid ─────────────────────────────────────────────────────── */}
      <div ref={movieGridRef} style={{ maxWidth: 1280, margin: "0 auto", padding: "28px clamp(16px, 4vw, 20px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 800, color: "#f0f0f0" }}>
            {gridHeading()}
          </h2>
          {!loading && (
            <span style={{ fontSize: 12, color: "#3a3a44", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {items.length} of {totalItems}
            </span>
          )}
        </div>

        <div className="movie-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 14, marginBottom: 28 }}>
          {loading ? (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "48px 0", color: "#3a3a44", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }}>
              loading…
            </div>
          ) : items.length === 0 ? (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "48px 0", color: "#3a3a44", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }}>
              nothing found yet — try syncing content from the admin endpoints
            </div>
          ) : (
            items.map(m => <MovieCard key={m.id} movie={m} basePath={basePath} />)
          )}
        </div>

        {/* Load more */}
        {!loading && currentPage < totalPages && (
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <button
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                background: loadingMore ? "#161618" : "transparent",
                border: "0.5px solid #252528",
                color: loadingMore ? "#3a3a44" : "#9898a6",
                padding: "11px 32px", borderRadius: 22,
                fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif",
                cursor: loadingMore ? "default" : "pointer",
                transition: "border-color 0.15s, color 0.15s",
              }}
              onMouseEnter={e => { if (!loadingMore) { e.currentTarget.style.borderColor = "#3a3a44"; e.currentTarget.style.color = "#c0c0cc"; } }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#252528"; e.currentTarget.style.color = "#9898a6"; }}
            >
              {loadingMore ? "loading…" : `load more · ${totalItems - items.length} remaining`}
            </button>
          </div>
        )}

        {/* ── Latest guff ────────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 800, color: "#f0f0f0" }}>
            latest guff
          </h2>
          <button onClick={() => navigate("/discussions")} style={{
            fontSize: 12, color: "#FF5C35", background: "transparent", border: "none", cursor: "pointer",
          }}>see all discussions →</button>
        </div>
        <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, overflow: "hidden" }}>
          {recentDiscussions.length === 0
            ? <div style={{ padding: "28px 20px", color: "#55555e", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>no discussions yet — be the first!</div>
            : recentDiscussions.map(d => (
                <DiscussionCard
                  key={d.id}
                  d={d}
                  onDelete={id => setRecentDiscussions(prev => prev.filter(x => x.id !== id))}
                />
              ))}
        </div>
      </div>
    </div>
  );
}